/**
 * useTenantFeatures — reads the caller's tenant feature_flags map from
 * `tenant_settings.feature_flags` and exposes a typed `has(flag)` helper.
 *
 * Director directive (2026-05-11): every contract-driven workflow on
 * the platform is tenant-toggleable via a snake_case key in this jsonb
 * map. Components that render contract-specific UI (e.g.
 * `FeeRequestSection`, `FeeRequestsPage`, future #75 abandoned-
 * belongings surfaces) gate themselves on the corresponding flag.
 *
 * Defaults to off while loading so the UI never flashes a feature
 * surface the tenant cannot actually use. RLS scopes the SELECT to
 * the caller's organization — no org arg required.
 *
 * Keys are canonicalised here so component code never types a raw
 * string and silently mismatches a never-set key. Adding a flag = add
 * an entry to `KNOWN_FLAGS` and the discriminated `TenantFeatureFlag`
 * union.
 */

import { useQuery } from '@tanstack/react-query'
import { useSupabase } from './useSupabase'

export const KNOWN_FLAGS = {
  /** Cleaner-side fee request + owner-approver decision flow (#76). */
  additional_fee_workflow: 'additional_fee_workflow',
  /** Abandoned-belongings reports + acknowledgment flow (#75). */
  abandoned_belongings_tracking: 'abandoned_belongings_tracking',
  /** Owner/admin bidding tool (saved bids + estimate engine). */
  bidding_tool: 'bidding_tool',
} as const

export type TenantFeatureFlag = keyof typeof KNOWN_FLAGS

interface UseTenantFeaturesResult {
  has:       (flag: TenantFeatureFlag) => boolean
  isLoading: boolean
}

type FlagMap = Partial<Record<TenantFeatureFlag, boolean>>

export function useTenantFeatures(): UseTenantFeaturesResult {
  const supabase = useSupabase()

  const { data, isLoading } = useQuery({
    queryKey:  ['tenant-feature-flags'],
    queryFn:   async (): Promise<FlagMap> => {
      const { data, error } = await supabase
        .from('tenant_settings')
        .select('feature_flags')
        .single()

      if (error) throw error
      const raw = (data as { feature_flags?: unknown } | null)?.feature_flags
      if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return {}

      const out: FlagMap = {}
      for (const key of Object.keys(KNOWN_FLAGS) as TenantFeatureFlag[]) {
        const val = (raw as Record<string, unknown>)[key]
        out[key] = val === true
      }
      return out
    },
    staleTime: 5 * 60 * 1000,
    retry:     2,
  })

  return {
    has: (flag) => data?.[flag] === true,
    isLoading,
  }
}
